import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Settings } from "lucide-react";
import { StickerConfiguration, StickerPaperSize, Product } from "@/types/stock";
import { stickerSizePresets } from "@/config/appConfig";
import { toast } from "@/hooks/use-toast";
import BarcodeSticker from "./BarcodeSticker";

// Sample product for preview
const sampleProduct: Product = {
  id: "preview-sample",
  name: "Sample Product",
  sku: "SKU-12345",
  barcode: "1234567890123",
  price: 99.999,
  cost: 50.0,
  stock: 100,
  minStock: 10,
  maxStock: 500,
  isActive: true,
  categoryId: "sample-category",
  category: {
    id: "sample-category",
    name: "Electronics",
    description: "Sample category",
    shopId: "sample-shop",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  shopId: "sample-shop",
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

interface StickerConfigDialogProps {
  config: StickerConfiguration;
  onConfigChange: (config: StickerConfiguration) => void;
  onReset: () => void;
}

const StickerConfigDialog: React.FC<StickerConfigDialogProps> = ({
  config,
  onConfigChange,
  onReset,
}) => {
  const { t } = useTranslation();
  const [localConfig, setLocalConfig] = useState<StickerConfiguration>(config);
  const [open, setOpen] = useState(false);

  const handleSave = () => {
    onConfigChange(localConfig);
    toast({
      title: t("stock.stickerConfigSaved"),
    });
    setOpen(false);
  };

  const handleReset = () => {
    onReset();
    setLocalConfig(config);
    toast({
      title: t("stock.stickerConfigReset"),
    });
  };

  const handlePaperSizeChange = (size: StickerPaperSize) => {
    const preset = stickerSizePresets[size];
    setLocalConfig({
      ...localConfig,
      paperSize: size,
      dimensions: preset
        ? { width: preset.width, height: preset.height }
        : localConfig.dimensions,
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="flex items-center gap-2">
          <Settings className="h-4 w-4" />
          {t("stock.stickerSettings")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("stock.configureStickerSettings")}</DialogTitle>
          <DialogDescription>
            {t("stock.configureStickerSettings")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Paper Size Selection */}
          <div className="space-y-2">
            <Label>{t("stock.paperSize")}</Label>
            <Select
              value={localConfig.paperSize}
              onValueChange={(value) =>
                handlePaperSizeChange(value as StickerPaperSize)
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(stickerSizePresets).map(([key, preset]) => (
                  <SelectItem key={key} value={key}>
                    {preset.label}
                  </SelectItem>
                ))}
                <SelectItem value="custom">{t("stock.customSize")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Custom Dimensions */}
          {localConfig.paperSize === "custom" && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("stock.width")} (mm)</Label>
                <Input
                  type="number"
                  min="20"
                  max="100"
                  value={localConfig.dimensions.width}
                  onChange={(e) =>
                    setLocalConfig({
                      ...localConfig,
                      dimensions: {
                        ...localConfig.dimensions,
                        width: parseInt(e.target.value) || 40,
                      },
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>{t("stock.height")} (mm)</Label>
                <Input
                  type="number"
                  min="15"
                  max="80"
                  value={localConfig.dimensions.height}
                  onChange={(e) =>
                    setLocalConfig({
                      ...localConfig,
                      dimensions: {
                        ...localConfig.dimensions,
                        height: parseInt(e.target.value) || 20,
                      },
                    })
                  }
                />
              </div>
            </div>
          )}

          {/* Display Options */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">
              {t("stock.displayOptions")}
            </Label>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="showProductName">
                  {t("stock.showProductName")}
                </Label>
                <Switch
                  id="showProductName"
                  checked={localConfig.displayOptions.showProductName}
                  onCheckedChange={(checked) =>
                    setLocalConfig({
                      ...localConfig,
                      displayOptions: {
                        ...localConfig.displayOptions,
                        showProductName: checked,
                      },
                    })
                  }
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="showSKU">{t("stock.showSKU")}</Label>
                <Switch
                  id="showSKU"
                  checked={localConfig.displayOptions.showSKU}
                  onCheckedChange={(checked) =>
                    setLocalConfig({
                      ...localConfig,
                      displayOptions: {
                        ...localConfig.displayOptions,
                        showSKU: checked,
                      },
                    })
                  }
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="showPrice">{t("stock.showPrice")}</Label>
                <Switch
                  id="showPrice"
                  checked={localConfig.displayOptions.showPrice}
                  onCheckedChange={(checked) =>
                    setLocalConfig({
                      ...localConfig,
                      displayOptions: {
                        ...localConfig.displayOptions,
                        showPrice: checked,
                      },
                    })
                  }
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="showBarcode">{t("stock.showBarcode")}</Label>
                <Switch
                  id="showBarcode"
                  checked={localConfig.displayOptions.showBarcode}
                  onCheckedChange={(checked) =>
                    setLocalConfig({
                      ...localConfig,
                      displayOptions: {
                        ...localConfig.displayOptions,
                        showBarcode: checked,
                      },
                    })
                  }
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="showCategory">{t("stock.showCategory")}</Label>
                <Switch
                  id="showCategory"
                  checked={localConfig.displayOptions.showCategory}
                  onCheckedChange={(checked) =>
                    setLocalConfig({
                      ...localConfig,
                      displayOptions: {
                        ...localConfig.displayOptions,
                        showCategory: checked,
                      },
                    })
                  }
                />
              </div>
            </div>
          </div>

          {/* Font Sizes */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">
              {t("stock.fontSizes")}
            </Label>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("stock.productNameSize")} (px)</Label>
                <Input
                  type="number"
                  min="4"
                  max="16"
                  value={localConfig.fontSize.productName}
                  onChange={(e) =>
                    setLocalConfig({
                      ...localConfig,
                      fontSize: {
                        ...localConfig.fontSize,
                        productName: parseInt(e.target.value) || 7,
                      },
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>{t("stock.priceSize")} (px)</Label>
                <Input
                  type="number"
                  min="4"
                  max="16"
                  value={localConfig.fontSize.price}
                  onChange={(e) =>
                    setLocalConfig({
                      ...localConfig,
                      fontSize: {
                        ...localConfig.fontSize,
                        price: parseInt(e.target.value) || 6,
                      },
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>{t("stock.skuSize")} (px)</Label>
                <Input
                  type="number"
                  min="3"
                  max="12"
                  value={localConfig.fontSize.sku}
                  onChange={(e) =>
                    setLocalConfig({
                      ...localConfig,
                      fontSize: {
                        ...localConfig.fontSize,
                        sku: parseInt(e.target.value) || 5,
                      },
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>{t("stock.otherTextSize")} (px)</Label>
                <Input
                  type="number"
                  min="3"
                  max="12"
                  value={localConfig.fontSize.other}
                  onChange={(e) =>
                    setLocalConfig({
                      ...localConfig,
                      fontSize: {
                        ...localConfig.fontSize,
                        other: parseInt(e.target.value) || 5,
                      },
                    })
                  }
                />
              </div>
            </div>
          </div>

          {/* Barcode & Margin Settings */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("stock.barcodeHeight")} (mm)</Label>
              <Input
                type="number"
                min="5"
                max="20"
                value={localConfig.barcodeHeight}
                onChange={(e) =>
                  setLocalConfig({
                    ...localConfig,
                    barcodeHeight: parseInt(e.target.value) || 8,
                  })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>{t("stock.margin")} (mm)</Label>
              <Input
                type="number"
                min="0"
                max="5"
                step="0.5"
                value={localConfig.margin}
                onChange={(e) =>
                  setLocalConfig({
                    ...localConfig,
                    margin: parseFloat(e.target.value) || 1,
                  })
                }
              />
            </div>
          </div>

          {/* Live Preview */}
          <div className="space-y-2">
            <Label className="text-base font-semibold">
              {t("stock.stickerPreview")}
            </Label>
            <Card>
              <CardContent className="p-4">
                <div className="flex justify-center items-center bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                  <BarcodeSticker
                    product={sampleProduct}
                    barcode={sampleProduct.barcode}
                    config={localConfig}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between pt-4">
            <Button variant="outline" onClick={handleReset}>
              {t("stock.resetToDefaults")}
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>
                {t("common.cancel")}
              </Button>
              <Button onClick={handleSave}>{t("common.save")}</Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StickerConfigDialog;
